/**
 * AutoTranslateManager.js
 *
 * Background component for translating dynamic content (chat messages, offers, descriptions).
 * - Scans visible text nodes in the document and translates them to the active UI language.
 * - Skips protected/control tags (input, button, script, etc.).
 * - Preserves original text on each node so we can restore it when language changes back.
 * - Uses i18next for language detection and translateService for actual translation.
 *
 * IMPORTANT:
 * - This component is for DYNAMIC content only. Use i18next for static UI text.
 * - React re-renders may reset translated content; the manager re-applies on language change.
 * - Does NOT render any UI; purely a background manager.
 *
 * Usage:
 *   // In App or root component (typically in index.js)
 *   <AutoTranslateManager />
 */

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { translateText } from "../utils/translateService";
import { warnDev } from "../utils/logger";

export default function AutoTranslateManager() {
  const { i18n } = useTranslation();
  const abortRef = useRef({ abort: false });

  useEffect(() => {
    // Reset abort flag for this effect
    abortRef.current.abort = false;

    // Tags to completely skip (never translate their content)
    const excludedTags = new Set([
      "SCRIPT",
      "STYLE",
      "CODE",
      "PRE",
      "TEXTAREA",
      "INPUT",
      "BUTTON",
      "SELECT",
      "OPTION",
      "SVG",
      "CANVAS",
      "IMG",
      "NOSCRIPT",
      "IFRAME",
    ]);

    /**
     * Check if a text node should be translated
     */
    function isTranslatableTextNode(node) {
      const txt = node.nodeValue;
      if (!txt) return false;

      const trimmed = txt.trim();
      // Ignore very short or very long text
      if (trimmed.length < 2 || trimmed.length > 1000) return false;

      // Require at least one letter/number/common unicode (avoid pure punctuation/emoji)
      if (!/[A-Za-z0-9\u0080-\u024F]/.test(trimmed)) return false;

      // Skip if inside excluded tags
      let el = node.parentElement;
      while (el) {
        if (excludedTags.has(el.tagName)) return false;
        // Skip if parent is marked with data-no-translate
        if (el.closest && el.closest("[data-no-translate]")) return false;
        el = el.parentElement;
      }

      return true;
    }

    /**
     * Collect all translatable text nodes from the DOM
     */
    function collectTextNodes() {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode(node) {
            return isTranslatableTextNode(node)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        }
      );

      const nodes = [];
      let node;
      while ((node = walker.nextNode())) {
        nodes.push(node);
      }
      return nodes;
    }

    /**
     * Translate all collected text nodes to the target language
     */
    async function translateNodesTo(targetLang) {
      // If target is English, restore original text
      if (!targetLang || targetLang.startsWith("en")) {
        const walker = document.createTreeWalker(
          document.body,
          NodeFilter.SHOW_TEXT,
          null
        );
        let node;
        while ((node = walker.nextNode())) {
          // Restore original if saved
          if (node.__origText !== undefined && node.nodeValue !== node.__origText) {
            node.nodeValue = node.__origText;
          }
        }
        return;
      }

      const nodes = collectTextNodes();
      if (!nodes.length) return;

      // Build translation tasks
      const tasks = [];
      for (const node of nodes) {
        // Save original text if not already saved
        if (node.__origText === undefined) {
          node.__origText = node.nodeValue;
        }

        const original = node.__origText;

        // Skip if already translated for this language (check parent dataset)
        const langKey = `translated${targetLang}`;
        if (
          node.parentElement &&
          node.parentElement.dataset &&
          node.parentElement.dataset[langKey]
        ) {
          continue; // Already translated; skip
        }

        tasks.push({ node, original });
      }

      // Translate with limited concurrency to avoid overwhelming the API
      const concurrencyLimit = 4;
      let taskIndex = 0;

      async function translateWorker() {
        while (
          taskIndex < tasks.length &&
          !abortRef.current.abort
        ) {
          const currentIndex = taskIndex++;
          const { node, original } = tasks[currentIndex];

          try {
            const translated = await translateText(original, targetLang);

            // Check abort and node connectivity
            if (abortRef.current.abort || !node.isConnected) return;

            // Update node if translation successful
            if (translated) {
              node.nodeValue = translated;

              // Mark as translated in parent dataset
              if (node.parentElement && node.parentElement.dataset) {
                node.parentElement.dataset[langKey] = "1";
              }
            }
          } catch (err) {
            warnDev(
              "[AutoTranslateManager] Translation error:",
              err
            );
            // Continue with other nodes
          }
        }
      }

      // Run multiple workers in parallel (with concurrency limit)
      const workerCount = Math.min(concurrencyLimit, tasks.length);
      await Promise.all(
        Array.from({ length: workerCount }, () => translateWorker())
      );
    }

    // Translate on mount and when language changes
    translateNodesTo(i18n.language).catch((err) =>
      warnDev("[AutoTranslateManager] Error:", err)
    );

    // Re-run on popstate (SPA navigation)
    const handlePopState = () => {
      if (!abortRef.current.abort) {
        translateNodesTo(i18n.language).catch(() => {});
      }
    };
    window.addEventListener("popstate", handlePopState);

    // Cleanup
    return () => {
      abortRef.current.abort = true;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [i18n.language]);

  // This component renders no UI
  return null;
}

